// components/ui/ConfirmationModal.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, Mail, LogIn, Clock, UserCheck } from 'lucide-react';
import Logo from './Logo';
import Button from './Button';

interface ConfirmationModalProps {
  email: string;
  userType: 'secretary' | 'supervisor';
  onClose: () => void;
  autoRedirect?: boolean;
  redirectDelay?: number;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  email,
  userType,
  onClose,
  autoRedirect = true,
  redirectDelay = 5000
}) => {
  const [countdown, setCountdown] = useState(Math.floor(redirectDelay / 1000));

  useEffect(() => {
    if (autoRedirect) {
      const timer = setTimeout(() => {
        onClose();
      }, redirectDelay);

      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [autoRedirect, onClose, redirectDelay]);

  return (
    <div className="fixed inset-0 bg-gray-50 overflow-y-auto">
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo size="xl" showText={true} />
          </div>
          
          <div className="mt-8 bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900">
                Inscription réussie !
              </h2>
              
              <p className="mt-3 text-lg text-gray-600 max-w-md mx-auto">
                Bienvenue dans notre plateforme. Votre compte a été créé avec succès.
              </p>
              
              <div className="mt-6 p-5 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-center mb-2">
                  <Mail className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-semibold">
                    Email de vérification envoyé
                  </span>
                </div>
                <p className="text-center font-medium text-gray-900 break-all bg-white py-2 px-4 rounded-lg border">
                  {email}
                </p>
                <p className="mt-3 text-sm text-blue-700">
                  Vérifiez votre boîte de réception et cliquez sur le lien pour confirmer votre email.
                </p>
              </div>
              
              <div className="mt-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center mb-3">
                  <UserCheck className="h-5 w-5 text-gray-700 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Prochaines étapes
                  </h3>
                </div>
                <ul className="space-y-3 text-left">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-gray-700">
                      <span className="font-medium">Vérifiez votre email</span> - Cliquez sur le lien reçu
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-gray-700">
                      {userType === 'secretary' ? (
                        <>
                          <span className="font-medium">Attendez la validation</span> - Votre superviseur validera votre compte
                        </>
                      ) : (
                        <>
                          <span className="font-medium">Configurez votre institution</span> - Après connexion
                        </>
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                    <span className="text-gray-700">
                      <span className="font-medium">Connectez-vous</span> - Utilisez vos identifiants
                    </span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-8 space-y-4">
                <Button
                  onClick={onClose}
                  variant="primary"
                  className="w-full py-3 text-base font-medium shadow-lg hover:shadow-xl transition-shadow"
                >
                  <LogIn className="h-5 w-5 inline-block mr-2" />
                  Se connecter maintenant
                </Button>
                
                {autoRedirect && (
                  <div className="flex items-center justify-center text-sm text-gray-500 bg-gray-50 py-2 rounded-lg">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>Redirection automatique dans {countdown} seconde{countdown !== 1 ? 's' : ''}...</span>
                  </div>
                )}
                
                <Button
                  onClick={onClose}
                  variant="secondary"
                  className="w-full py-3 text-base font-medium"
                >
                  Fermer cette page
                </Button>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Vous n'avez pas reçu l'email ? Vérifiez votre dossier spam ou{' '}
                  <button className="text-blue-600 hover:text-blue-800 font-medium">
                    renvoyer l'email
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;